//React
import React, { useEffect, useState } from "react";
import * as ReactDOMServer from "react-dom/server";
//Leaflet
import { MapContainer, TileLayer, Polyline, Marker } from "react-leaflet";
import "leaflet.vectorgrid";
import "leaflet-gpx";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import L from "leaflet";
import type { LatLngExpression } from "leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
//Icons
import { FaPlay } from "react-icons/fa";
import { FaFlagCheckered } from "react-icons/fa";
//Internal utilities
import { NodePoint, Place, MapProps } from "../../types/types";
import {
  fetchGpx,
  getRouteAndCenter,
} from "./routeMapHelpers";
import { getPlacesByLocation } from "../../services/getPlacesByLocation";
import configService from "../../services/configService";
import { poiService } from "../../services/poiService";
import { NodePointMarker } from "../NodePointMarker";

// Styles
import "./routeMap.css";

export const RouteMap: React.FC<MapProps> = ({ gpxUrl, isMobile, isAdmin = false, onCreatePoi }) => {
  const [routeLine, setRouteLine] = useState<Array<LatLngExpression>>([]);
  const [center, setCenter] = useState<any>([43.0, -3.086468]);
  const [places, setPlaces] = useState<Place[]>([]);
  const [tenantPois, setTenantPois] = useState<NodePoint[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const osm_url = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
  const attr =
    '&copy; <a href="http://osm.org/copyright%22%3EOpenStreetMap</a> contributors';

  useEffect(() => {
    if (!gpxUrl) return;

    (async () => {
      try {
        setPlaces([]);
        setTenantPois([]);
        setError(null);
        setIsLoading(true);
        const data = await fetchGpx(gpxUrl);

        const { routeLine, mapCenter } = getRouteAndCenter(data, isMobile);

        setRouteLine(routeLine);
        setCenter(mapCenter);

        if (isAdmin) {
          const adminPlaces = await getPlacesByLocation({ lat: mapCenter.lat, lng: mapCenter.lon });
          setPlaces(adminPlaces);
        } else {
          await configService.initialize();
          const tenantId = configService.getConfig().tenantId;
          const pois = await poiService.getTenantPois(tenantId);
          setTenantPois(pois);
        }

        setIsLoading(false);
      } catch (err: any) {
        console.error("Error loading GPX file:", err);
        setError(err?.message ?? String(err));
        setIsLoading(false);
      }
    })();
  }, [gpxUrl, isMobile]);

  if (!gpxUrl) {
    return <div className="map-loading">No route selected</div>;
  }

  if (isLoading) {
    return <div className="map-loading">Loading map...</div>;
  }

  if (error) {
    return <div className="map-loading">Error: {error}</div>;
  }

  const startIcon = L.divIcon({
    className: "",
    html: ReactDOMServer.renderToString(
      <div className="route-endpoint-marker route-start">
        <FaPlay />
      </div>
    ),
    iconAnchor: [15, 15],
  });

  const finishIcon = L.divIcon({
    className: "",
    html: ReactDOMServer.renderToString(
      <div className="route-endpoint-marker route-finish">
        <FaFlagCheckered />
      </div>
    ),
    iconAnchor: [15, 15],
  });

  const startPoint = routeLine[0] as { lat: number; lng: number } | undefined;
  const finishPoint = routeLine[routeLine.length - 1] as { lat: number; lng: number } | undefined;

  const poiMarkers = isAdmin
    ? places.map((p) => (
        <NodePointMarker
          key={p.displayName.text}
          place={p}
          currentZoom={13}
          isMapClicked={false}
          isAdmin={isAdmin}
          onCreatePoi={onCreatePoi}
        />
      ))
    : tenantPois.map((poi) => (
        <NodePointMarker
          key={poi.id}
          node={poi}
          currentZoom={13}
          isMapClicked={false}
        />
      ));

  return (
    <div className="map">
      <MapContainer key={center} center={center} zoom={12}>
        <TileLayer attribution={attr} url={osm_url} />
        <Polyline pathOptions={{ color: "black" }} positions={routeLine} />

        {startPoint && <Marker position={[startPoint.lat, startPoint.lng]} icon={startIcon} />}
        {finishPoint && <Marker position={[finishPoint.lat, finishPoint.lng]} icon={finishIcon} />}

        {poiMarkers.length > 0 && (
          <MarkerClusterGroup
            chunkedLoading
            disableClusteringAtZoom={15}
            spiderfyOnMaxZoom
            showCoverageOnHover={false}
            maxClusterRadius={48}
          >
            {poiMarkers}
          </MarkerClusterGroup>
        )}
      </MapContainer>
    </div>
  );
};
